import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MetricChartSpec } from '@/components/charts/generic';
import type { DomainFeedConversionRateDay } from '@/domain/biomass';
import type {
    DomainBehaviourDay,
    DomainBiomassDay,
    DomainBioscopeDay,
} from '@/domain/cages/overview.types';
import { parseLocale } from '@/utils/localeFormatters';
import { resolveSceneLabelGraphName } from '@/utils/sceneLabelHelpers';

import {
    buildCoefficientOfVariationChartSpec,
    buildConditionFactorChartSpec,
    buildDepthTemperatureChartSpec,
    buildFeedConversionRatioChartSpec,
    buildGrowthChartSpec,
    buildLivingWeightChartSpec,
    buildMeasurementsChartSpec,
    buildPitchRollChartSpec,
    buildSceneLabelsChartSpec,
    buildSizeChartSpec,
    buildSwimSpeedChartSpec,
    buildVerticalDisparityChartSpec,
} from './chartSpecs';

export interface UseOverviewChartSpecsParams {
    /** Weight-model rows. Empty without the biomass licence. */
    days: DomainBiomassDay[];
    /**
     * Camera telemetry rows. Carries depth, temperature and the diagnostics, and
     * is not licence-gated. Defaults to empty for pages that chart none of it.
     */
    bioscopeDays?: DomainBioscopeDay[];
    /** Swim-speed rows. Empty without a behaviour licence. */
    behaviourDays?: DomainBehaviourDay[];
    /** Dayparts that get a series, from `filterGraphDayparts`. */
    graphedDaypartTags: string[];
    /** Dayparts that start visible. */
    activeDaypartTags: string[];
    /** The single daypart the charts that do not fan out should plot. */
    daypartTag: string;
    weightUnitTag: string;
    /** From a separate endpoint. Defaults to empty for pages that omit the chart. */
    feedConversionRateDays?: DomainFeedConversionRateDay[];
}

export interface OverviewChartSpecs {
    livingWeight: MetricChartSpec<'line'>;
    measurements: MetricChartSpec<'bar'>;
    growth: MetricChartSpec<'line'>;
    depthTemperature: MetricChartSpec<'line'>;
    size: MetricChartSpec<'line'>;
    coefficientOfVariation: MetricChartSpec<'line'>;
    conditionFactor: MetricChartSpec<'line'>;
    feedConversionRatio: MetricChartSpec<'line'>;
    swimSpeed: MetricChartSpec<'line'>;
    // Admin-only diagnostics.
    pitchRoll: MetricChartSpec<'line'>;
    sceneLabels: MetricChartSpec<'line'>;
    verticalDisparity: MetricChartSpec<'line'>;
}

const NO_DAYS: never[] = [];

/**
 * Builds every cage overview chart spec, resolving translations and locale.
 *
 * Pages destructure the charts they render. Each spec is a small pure object, so
 * building the few a page does not use costs nothing measurable and keeps the
 * chart-to-translation wiring in one place rather than copied per page.
 *
 * The shape of each chart lives in its builder under `chartSpecs`; this hook only
 * supplies translated labels and memoises the results.
 */
export function useOverviewChartSpecs(params: UseOverviewChartSpecsParams): OverviewChartSpecs {
    const {
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
        feedConversionRateDays = NO_DAYS,
        bioscopeDays = NO_DAYS,
        behaviourDays = NO_DAYS,
    } = params;
    const { t, i18n } = useTranslation('cageCharts');
    const { t: tRoot } = useTranslation();
    const locale = parseLocale(i18n.language);

    const daypartParams = useMemo(
        () => ({
            days,
            graphedDaypartTags,
            activeDaypartTags,
            daypartLabel: (tag: string) => tRoot(`daypartTag.${tag}`),
            locale,
        }),
        [days, graphedDaypartTags, activeDaypartTags, tRoot, locale]
    );

    const livingWeight = useMemo(
        () =>
            buildLivingWeightChartSpec({
                ...daypartParams,
                axisLabel: t('axis.weight'),
                trendLabel: tRoot('daypartTag.trend_line'),
                weightUnitTag,
            }),
        [daypartParams, t, tRoot, weightUnitTag]
    );

    const measurements = useMemo(
        () => buildMeasurementsChartSpec({ ...daypartParams, axisLabel: t('axis.count') }),
        [daypartParams, t]
    );

    const growth = useMemo(
        () =>
            buildGrowthChartSpec({ ...daypartParams, axisLabel: t('axis.growth'), weightUnitTag }),
        [daypartParams, t, weightUnitTag]
    );

    const depthTemperature = useMemo(
        () =>
            buildDepthTemperatureChartSpec({
                days: bioscopeDays,
                daypartTag,
                depthLabel: t('axis.depth'),
                temperatureLabel: t('axis.temperature'),
                minLabel: t('series.min'),
                maxLabel: t('series.max'),
                averageLabel: t('series.average'),
                locale,
            }),
        [bioscopeDays, daypartTag, t, locale]
    );

    const size = useMemo(
        () =>
            buildSizeChartSpec({
                days,
                heightLabel: t('axis.height'),
                lengthLabel: t('axis.length'),
                locale,
            }),
        [days, t, locale]
    );

    const coefficientOfVariation = useMemo(
        () =>
            buildCoefficientOfVariationChartSpec({
                ...daypartParams,
                axisLabel: t('axis.coefficientOfVariation'),
            }),
        [daypartParams, t]
    );

    const conditionFactor = useMemo(
        () => buildConditionFactorChartSpec({ days, axisLabel: t('axis.conditionFactor'), locale }),
        [days, t, locale]
    );

    const feedConversionRatio = useMemo(
        () =>
            buildFeedConversionRatioChartSpec({
                days: feedConversionRateDays,
                axisLabel: t('axis.feedConversionRatio'),
                locale,
            }),
        [feedConversionRateDays, t, locale]
    );

    const swimSpeed = useMemo(
        () =>
            buildSwimSpeedChartSpec({
                ...daypartParams,
                days: behaviourDays,
                axisLabel: t('axis.swimSpeed'),
            }),
        [daypartParams, behaviourDays, t]
    );

    const pitchRoll = useMemo(
        () =>
            buildPitchRollChartSpec({
                days: bioscopeDays,
                axisLabel: t('axis.angle'),
                pitchLabel: t('series.pitch'),
                rollLabel: t('series.roll'),
                locale,
            }),
        [bioscopeDays, t, locale]
    );

    const sceneLabels = useMemo(
        () =>
            buildSceneLabelsChartSpec({
                days: bioscopeDays,
                daypartTag,
                axisLabel: t('axis.sceneLabelShare'),
                // Resolves its own translations rather than taking `t`: it looks a
                // key up in two namespaces and falls back between them, and appends
                // the raw tag for an aliased one — `Too close (chaos)`.
                sceneLabelLabel: resolveSceneLabelGraphName,
                locale,
            }),
        // `t` stays in the deps so the labels re-resolve on a language change,
        // even though `resolveSceneLabelGraphName` reads i18next directly.
        [bioscopeDays, daypartTag, t, locale]
    );

    const verticalDisparity = useMemo(
        () =>
            buildVerticalDisparityChartSpec({
                ...daypartParams,
                days: bioscopeDays,
                axisLabel: t('axis.verticalDisparity'),
            }),
        [daypartParams, t]
    );

    return {
        livingWeight,
        measurements,
        growth,
        depthTemperature,
        size,
        coefficientOfVariation,
        conditionFactor,
        feedConversionRatio,
        swimSpeed,
        pitchRoll,
        sceneLabels,
        verticalDisparity,
    };
}
